import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { QuizManagementService } from '@edtech-ui/core/services/quiz-management/quiz-management.service';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';

@Component({
  selector: 'app-extend-link-dialog-box',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './extend-link-dialog-box.component.html',
  styleUrl: './extend-link-dialog-box.component.scss'
})
export class ExtendLinkDialogBoxComponent implements OnInit, OnDestroy {

  newExpiryDate: string | null = null;
  minExpiryDate: string = '';
  selectedQuickExtend: number | null = null;
  isExtending = false;
  subscription = new Subscription();

  constructor(
    private dialogRef: MatDialogRef<ExtendLinkDialogBoxComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { linkId: string, linkName: string, currentExpiry: string },
    private quizService: QuizManagementService,
    private toastr: ToastrService
  ) { }

  ngOnInit() {
    this.minExpiryDate = this.getCurrentDateTimeLocal();
    if (this.data && this.data.currentExpiry) {
        const current = new Date(this.data.currentExpiry);
        if (!isNaN(current.getTime())) {
            this.newExpiryDate = this.getCurrentDateTimeLocal(current);
        }
    }
  }

  isExpired(): boolean {
    if (!this.data || !this.data.currentExpiry) return false;
    const current = new Date(this.data.currentExpiry);
    if (isNaN(current.getTime())) return false;
    return current.getTime() < Date.now();
  }

  getCurrentDateTimeLocal(d = new Date()): string {
    if (!(d instanceof Date) || isNaN(d.getTime())) {
       d = new Date();
    }
    const tzoffset = d.getTimezoneOffset() * 60000;
    const localISOTime = (new Date(d.getTime() - tzoffset)).toISOString().slice(0, 16);
    return localISOTime;
  }

  quickExtend(days: number) {
    if (days <= 0) return;
    this.selectedQuickExtend = days;
    let baseDate = new Date();
    baseDate.setDate(baseDate.getDate() + days);
    this.newExpiryDate = this.getCurrentDateTimeLocal(baseDate);
  }

  get isValid(): boolean {
    if (!this.newExpiryDate) return false;
    
    const selectedTime = new Date(this.newExpiryDate).getTime();
    const nowTime = Date.now();
    
    if (isNaN(selectedTime) || selectedTime <= nowTime) {
        return false;
    }
    
    if (this.data && this.data.currentExpiry) {
        const currentObj = new Date(this.data.currentExpiry);
        if (!isNaN(currentObj.getTime())) {
           const currentMins = Math.floor(currentObj.getTime() / 60000);
           const selectedMins = Math.floor(selectedTime / 60000);
           if (currentMins === selectedMins) {
               return false;
           }
        }
    }

    return true;
  }

  extendExpiry() {
    if (!this.isValid || !this.newExpiryDate) return;
    
    this.isExtending = true;
    let end_date_iso = '';
    
    try {
       end_date_iso = new Date(this.newExpiryDate).toISOString();
    } catch (e) {
       this.toastr.error('Invalid date format selected.');
       this.isExtending = false;
       return;
    }
    
    this.subscription.add(
      this.quizService.updateQuizLink(this.data.linkId, { end_date: end_date_iso }).pipe(
        tap((response: any) => {
          if (response && response.Success) {
            this.toastr.success('Link expiry extended successfully!');
            this.dialogRef.close(true);
          } else {
            this.toastr.error(response?.Message || 'Failed to extend link expiry');
          }
        }),
        catchError((err) => {
          this.toastr.error('Something went wrong while extending expiry');
          return of(null);
        }),
        finalize(() => {
          this.isExtending = false;
        })
      ).subscribe()
    );
  }

  closeDialog() {
    this.dialogRef.close();
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }
}
